<!--
Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
Unauthorized use, copying, distribution, displaying, or public performance
of this file, via any medium, is strictly prohibited. All information
contained herein is proprietary and confidential and may not be shared
with any third party without the express written consent of Reciprocity, Inc.
-->

<template>
  <b-form ref="form" :validated="attempted" class="mt-4">
    <zen-form-group label="Recipient type">
      <b-form-radio-group v-model="value.internal" @change="changeRecipientType">
        <b-form-radio :value="false">External</b-form-radio>
        <b-form-radio :value="true">Internal</b-form-radio>
      </b-form-radio-group>
    </zen-form-group>
    <component
      :is="`RecipientList${value.internal ? 'Internal' : 'External'}`"
      v-model="value.recipients"
      :internal="value.internal"
      :users="users"
      :validated="attempted"
      @input="updateValidity"
    />
    <zen-form-group label="CC to">
      <cc-list v-model="value.ccs" :users="users" @input="updateValidity" />
    </zen-form-group>
    <zen-form-group
      label="Questionnaire Email Template"
      tooltip="Select an email template to populate the email content below. Email templates can be created from the Tools page."
    >
      <dropdown-presenter :options-url="apiEndpoints.surveyEmailTemplateList" @input="fillEmailData" />
    </zen-form-group>
    <zen-form-group label="Title/Subject">
      <b-form-input v-model="value.title" placeholder="Add a title/subject" required @input="updateValidity" />
    </zen-form-group>
    <zen-form-group label="Greeting">
      <b-form-input v-model="value.salutations" placeholder="Default greeting of ‘Hi John Doe’ if left blank" />
    </zen-form-group>
    <zen-form-group label="Message">
      <rich-text
        ref="messageText"
        v-model="value.message"
        :rows="5"
        placeholder="Add a message"
        required="required"
        @input="updateValidity"
      />
    </zen-form-group>
    <zen-form-group label="Signature">
      <rich-text
        ref="greetingText"
        v-model="greetingText"
        :rows="2"
        placeholder="Add a signature"
        required="required"
        @input="updateValidity"
      />
    </zen-form-group>
    <zen-form-group label="Reply-To email">
      <b-form-input
        v-model="value.contactEmail"
        type="email"
        placeholder="Enter contact/reply-to email"
        required
        @input="updateValidity"
      />
    </zen-form-group>

    <zen-form-group label="Due date">
      <edit-card-date :value="value.autoNagDate" subtype="date" name="dueDate" @input="onChangeAutoNagDate" />
    </zen-form-group>

    <b-form-checkbox v-model="value.notifyOnResponse">
      I would like to be notified when the recipient responds to the questionnaire
    </b-form-checkbox>

    <b-form-checkbox v-model="autoNag" :disabled="!value.autoNagDate" class="resend" @change="onChangeAutoNag($event)">
      <div class="resend-label-wrapper">
        Resend every
        <b-form-select
          v-model="autoNagRecurrence"
          :disabled="!value.autoNagDate"
          class="resend-days"
          @change="onChangeAutoNagRecurrence($event)"
        >
          <option :value="1" selected>1</option>
          <option :value="2">2</option>
          <option :value="3">3</option>
          <option :value="4">4</option>
          <option :value="5">5</option>
          <option :value="6">6</option>
          <option :value="7">7</option>
        </b-form-select>
        days after due date
      </div>
    </b-form-checkbox>
  </b-form>
</template>

<script>
import {
  BForm,
  BFormInput,
  BFormRow,
  BFormTextarea,
  BFormCheckbox,
  BFormSelect,
  BFormRadio,
  BFormRadioGroup,
} from 'bootstrap-vue';
import { ZenFormGroup } from 'zenComponents';
import { mapGetters } from 'vuex';
import EditCardDate from '@/components/cards/EditCard/components/EditCardDate.vue';
import RecipientListExternal from './components/RecipientListExternal.vue';
import RecipientListInternal from './components/RecipientListInternal.vue';
import CcList from './components/CcList.vue';
import DropdownPresenter from '@/components/presenters/DropdownPresenter.vue';
import RichText from '@/components/rich_text/RichText.vue';

export default {
  name: 'RecipientDetails',
  components: {
    BForm,
    BFormCheckbox,
    BFormSelect,
    ZenFormGroup,
    BFormInput,
    BFormRadio,
    BFormRadioGroup,
    BFormRow,
    BFormTextarea,
    EditCardDate,
    RecipientListInternal,
    RecipientListExternal,
    CcList,
    DropdownPresenter,
    RichText,
  },
  props: {
    attempted: {
      type: Boolean,
      required: true,
    },
    users: {
      type: Array,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      autoNag: false,
      autoNagRecurrence: 1,
    };
  },
  computed: {
    ...mapGetters({
      featureFlags: 'featureFlags',
      apiEndpoints: 'surveySendingApiEndpoints',
    }),
    greetingText: {
      get() {
        return this.value.greeting || '';
      },
      set(newValue) {
        this.value.greeting = newValue;
      },
    },
  },
  methods: {
    updateValidity() {
      // Doing this on $nextTick to make sure form is in sync with model
      this.$nextTick(() => {
        const formValidity = this.$refs.form.checkValidity();
        const recipientValidity = this.checkRecipientValidity();
        const valid = this.featureFlags.questionnaire_improvements_external_recipients
          ? formValidity
          : formValidity && recipientValidity;
        this.$emit('update-validity', valid);
      });
    },
    changeRecipientType(internal) {
      let recipients = [];

      if (!this.featureFlags.questionnaire_improvements_external_recipients) {
        if (!internal) {
          recipients = [{ name: '', email: '' }];
        }
      }

      this.$emit('input', {
        ...this.value,
        recipients,
      });
      this.updateValidity();
    },
    checkRecipientValidity() {
      return this.value.recipients.length;
    },
    fillEmailData(selectedTemplate) {
      this.$emit('update-email-data', selectedTemplate);
    },
    onChangeAutoNagDate(val) {
      this.value.autoNagDate = val;
      if (!val) {
        this.resetAutoNag();
      }
    },
    onChangeAutoNag(val) {
      this.value.autoNagRecurrence = val ? this.autoNagRecurrence : 0;
    },
    onChangeAutoNagRecurrence(val) {
      if (this.autoNag) {
        this.value.autoNagRecurrence = val;
      }
    },
    resetAutoNag() {
      this.value.autoNagRecurrence = 0;
      this.autoNag = false;
      this.autoNagRecurrence = 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.resend {
  margin-top: 15px;
  &-days {
    display: inline-block;
    width: 55px;
    margin: 0 2px;
  }
  &-label-wrapper {
    position: relative;
    top: -3px;
  }
}
</style>
